import { cn } from "@/lib/utils";

/**
 * Gravun's consistent status system. Every status rendered anywhere in the
 * product maps to one of five restrained tones, no rainbow dashboards.
 */
export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

const TONE_CLASSES: Record<StatusTone, string> = {
  success: "bg-success/10 text-success border-success/25",
  warning: "bg-warning/15 text-warning-foreground border-warning/40",
  danger: "bg-destructive/10 text-destructive border-destructive/25",
  info: "bg-info/10 text-info border-info/25",
  neutral: "bg-surface-muted text-muted-foreground border-border",
};

/** Central mapping of known domain statuses to tones. */
export const STATUS_TONES: Record<string, StatusTone> = {
  // Contact/lead temperature
  hot: "danger",
  warm: "warning",
  cold: "info",
  "in-contract": "success",
  // Deal stages
  opportunity: "neutral",
  "proposal-sent": "info",
  "in-negociation": "warning",
  won: "success",
  lost: "neutral",
  delayed: "warning",
  // Future: estimates (draft/sent/viewed/accepted/declined/expired),
  // invoices (draft/issued/partially-paid/paid/overdue/void),
  // jobs (new/scheduled/in-progress/completed/cancelled).
};

export const StatusBadge = ({
  label,
  tone,
  status,
  className,
}: {
  label: string;
  /** Explicit tone, or derive from a known status key. */
  tone?: StatusTone;
  status?: string;
  className?: string;
}) => {
  const resolved: StatusTone =
    tone ?? (status ? (STATUS_TONES[status] ?? "neutral") : "neutral");
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center rounded-md border px-2 text-[11.5px] font-medium leading-none",
        TONE_CLASSES[resolved],
        className,
      )}
    >
      {label}
    </span>
  );
};
